import { Check, Pencil, type LucideIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { Fragment, useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useIsPhone } from '@/lib/platform'
import { cn } from '@/lib/utils'

// lead: something drawn before the label in place of an icon (a circle's colour). edit: a second, smaller button at the
// end of the row for changing the thing itself rather than going to it (a circle's quick edit).
export type Action = {
  label: string
  icon?: LucideIcon
  lead?: ReactNode
  current?: boolean
  onSelect: () => void
  edit?: { label: string; onSelect: () => void }
}
// A group can be named, when its actions would read as something else beside the others (spaces beside circles).
export type ActionGroup = Action[] | { heading: string; actions: Action[] }

// A short list of things to do: a menu by the button on a computer, and on a phone a sheet from the bottom edge, where
// the thumb already is (an action sheet on iOS, a bottom sheet on Android; styles/phone.css). Groups are set apart
// by a line, or by their heading.
export function ActionMenu({
  title,
  label,
  trigger,
  groups,
  side,
  align = 'start',
}: {
  // What the sheet is called, for screen readers, and a heading shown above the actions when there's more to say.
  title: string
  label?: ReactNode
  trigger: ReactElement
  groups: ActionGroup[]
  side?: 'top' | 'right' | 'bottom'
  align?: 'start' | 'end'
}) {
  const phone = useIsPhone()
  const [open, setOpen] = useState(false)
  const shown = groups
    .map((g) => (Array.isArray(g) ? { heading: undefined, actions: g } : g))
    .filter((g) => g.actions.length > 0)

  if (!phone)
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent side={side} align={align} className="w-60">
          {label && (
            <>
              <DropdownMenuLabel>{label}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {shown.map((group, i) => (
            <Fragment key={i}>
              {i > 0 && <DropdownMenuSeparator />}
              {group.heading && (
                <DropdownMenuLabel className="text-muted-foreground">{group.heading}</DropdownMenuLabel>
              )}
              {group.actions.map((action, j) => {
                const item = (
                  <DropdownMenuItem key={j} onSelect={action.onSelect} className={cn(action.edit && 'flex-1')}>
                    {action.lead}
                    {action.icon && <action.icon aria-hidden />}
                    <span className="min-w-0 flex-1 truncate">{action.label}</span>
                    {action.current && <Check aria-hidden />}
                  </DropdownMenuItem>
                )
                if (!action.edit) return item
                // Two items side by side, so the arrow keys reach the edit button too.
                return (
                  <div key={j} className="flex items-center">
                    {item}
                    <DropdownMenuItem aria-label={action.edit.label} onSelect={action.edit.onSelect}>
                      <Pencil aria-hidden />
                    </DropdownMenuItem>
                  </div>
                )
              })}
            </Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )

  // Up and down move between the actions, as in a menu.
  const arrows = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[role=menuitem]')]
    const at = items.indexOf(document.activeElement as HTMLElement)
    items[(at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus()
  }

  const row =
    'flex items-center gap-4 text-left text-body outline-none focus-visible:bg-accent active:bg-accent android:rounded-xl [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground'

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogContent showCloseButton={false} aria-describedby={undefined} className="gap-3 pt-9">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {label && <div className="px-1 pb-1 text-sub text-muted-foreground">{label}</div>}
        <div role="menu" aria-label={title} onKeyDown={arrows} className="flex flex-col gap-3">
          {shown.map((group, i) => (
            <div key={i} role="group" aria-label={group.heading} className="flex flex-col gap-1.5">
              {group.heading && <p className="px-1 text-sub text-muted-foreground">{group.heading}</p>}
              <div className="flex flex-col overflow-hidden rounded-2xl bg-secondary android:bg-transparent">
                {group.actions.map((action, j) => (
                  <div
                    key={j}
                    className="flex min-h-14 items-stretch border-b border-background last:border-b-0 android:border-b-0"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOpen(false)
                        action.onSelect()
                      }}
                      className={cn(row, 'flex-1 px-4 android:px-3')}
                    >
                      {action.lead}
                      {action.icon && <action.icon aria-hidden />}
                      <span className="min-w-0 flex-1 truncate">{action.label}</span>
                      {action.current && (
                        <>
                          <Check aria-hidden className="text-foreground!" />
                          <span className="sr-only">(current)</span>
                        </>
                      )}
                    </button>
                    {action.edit && (
                      <button
                        type="button"
                        role="menuitem"
                        aria-label={action.edit.label}
                        onClick={() => {
                          setOpen(false)
                          action.edit!.onSelect()
                        }}
                        className={cn(row, 'w-14 justify-center border-l border-background android:border-l-0')}
                      >
                        <Pencil aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {/* iOS ends an action sheet with Cancel, set apart; Android closes by a tap outside or the back gesture. */}
        <DialogPrimitive.Close className="flex min-h-14 items-center justify-center rounded-2xl bg-secondary font-semibold outline-none focus-visible:bg-accent active:bg-accent android:hidden">
          Cancel
        </DialogPrimitive.Close>
      </DialogContent>
    </Dialog>
  )
}
